import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Checkbox } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useG305Query } from './hooks/useG305Query';
import type { ReportRow, CurrencySummary, DelayStats } from '../../services/g305Api';
import './G305Form.css';

const reportTypeOptions = [
  { label: '日期別', value: 'date' },
  { label: '客戶別', value: 'customer' },
  { label: '部門別', value: 'dept' },
  { label: '繳款類別', value: 'category' },
  { label: '銀行別', value: 'bank' },
  { label: '水單號碼', value: 'slip' },
];

const G305Form: React.FC = () => {
  const { loading, data, currencySummary, twdTotal, groupCounts, delayStats, queried, noData, runQuery, reset } = useG305Query();

  const [reportTypes, setReportTypes] = useState<string[]>(['date']);
  const [customerFrom, setCustomerFrom] = useState('');
  const [customerTo, setCustomerTo] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [bankFrom, setBankFrom] = useState('');
  const [bankTo, setBankTo] = useState('');
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');
  const [categoryFrom, setCategoryFrom] = useState('');
  const [categoryTo, setCategoryTo] = useState('');
  const [slipNoFrom, setSlipNoFrom] = useState('');
  const [slipNoTo, setSlipNoTo] = useState('');

  const handleQuery = () => {
    runQuery({
      report_types: reportTypes,
      customer_from: customerFrom.trim() || undefined,
      customer_to: customerTo.trim() || undefined,
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
      bank_from: bankFrom.trim() || undefined,
      bank_to: bankTo.trim() || undefined,
      dept_from: deptFrom.trim() || undefined,
      dept_to: deptTo.trim() || undefined,
      category_from: categoryFrom.trim() || undefined,
      category_to: categoryTo.trim() || undefined,
      slip_no_from: slipNoFrom.trim() || undefined,
      slip_no_to: slipNoTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setReportTypes(['date']);
    setCustomerFrom(''); setCustomerTo('');
    setDateFrom(''); setDateTo('');
    setBankFrom(''); setBankTo('');
    setDeptFrom(''); setDeptTo('');
    setCategoryFrom(''); setCategoryTo('');
    setSlipNoFrom(''); setSlipNoTo('');
    reset();
  };

  const columns: ColumnsType<ReportRow> = [
    { title: '*', dataIndex: 'FG110_MK', key: 'FG110_MK', width: 30 },
    { title: '繳款單號', dataIndex: 'FG110_01', key: 'FG110_01', width: 110 },
    { title: '繳款日期', dataIndex: 'FG110_11', key: 'FG110_11', width: 100 },
    { title: '打單日期', dataIndex: 'FG110_32', key: 'FG110_32', width: 100 },
    { title: '延遲', dataIndex: 'delay_days', key: 'delay_days', width: 50, align: 'right' as const },
    { title: '部門', key: 'dept', width: 80, render: (_: unknown, r: ReportRow) => r.dept_name || r.FG110_19 },
    { title: '客戶/員工', key: 'customer', width: 120, render: (_: unknown, r: ReportRow) => r.customer_name || r.FG110_26 },
    { title: '類別', dataIndex: 'category_name', key: 'category_name', width: 80 },
    { title: '幣別', dataIndex: 'FG110_25', key: 'FG110_25', width: 50 },
    { title: '原幣繳款', dataIndex: 'FG110_12', key: 'FG110_12', width: 110, align: 'right' as const, render: (v: number) => v?.toLocaleString('zh-TW', { minimumFractionDigits: 2 }) },
    { title: '原幣入帳', dataIndex: 'FG110_22', key: 'FG110_22', width: 110, align: 'right' as const, render: (v: number) => v?.toLocaleString('zh-TW', { minimumFractionDigits: 2 }) },
    { title: '台幣入帳', dataIndex: 'FG110_NT', key: 'FG110_NT', width: 110, align: 'right' as const, render: (v: number) => v?.toLocaleString('zh-TW') },
    { title: '銀行', key: 'bank', width: 100, render: (_: unknown, r: ReportRow) => r.bank_name || r.FG111_15 },
    { title: '水單號碼', dataIndex: 'FG111_171', key: 'FG111_171', width: 100 },
    { title: '打單人員', dataIndex: 'F901_11', key: 'F901_11', width: 80 },
  ];

  const currColumns: ColumnsType<CurrencySummary> = [
    { title: '幣別', dataIndex: 'FG110_25', key: 'FG110_25', width: 60 },
    { title: '原幣繳款金額', dataIndex: 'total_orig_pay', key: 'total_orig_pay', align: 'right' as const, render: (v: number) => v?.toLocaleString('zh-TW', { minimumFractionDigits: 2 }) },
    { title: '原幣實際入帳', dataIndex: 'total_orig_act', key: 'total_orig_act', align: 'right' as const, render: (v: number) => v?.toLocaleString('zh-TW', { minimumFractionDigits: 2 }) },
    { title: '台幣實際入帳', dataIndex: 'total_twd_act', key: 'total_twd_act', align: 'right' as const, render: (v: number) => v?.toLocaleString('zh-TW') },
  ];

  const delayColumns: ColumnsType<DelayStats> = [
    { title: '員工代號', dataIndex: 'employee_code', key: 'employee_code', width: 100 },
    { title: '筆數', dataIndex: 'record_count', key: 'record_count', width: 80, align: 'right' as const },
    { title: '延遲天數', dataIndex: 'total_delay', key: 'total_delay', width: 100, align: 'right' as const },
    { title: '平均延遲', dataIndex: 'avg_delay', key: 'avg_delay', width: 100, align: 'right' as const },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.G305} action="query">
      <div className="g305-container">
        <div className="g305-header">
          <h2>繳款單明細表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={() => window.print()}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('印表項目 (至少勾選一項)')}
            <Checkbox.Group
              options={reportTypeOptions}
              value={reportTypes}
              onChange={vals => setReportTypes(vals as string[])}
            />
          </div>
          <Space wrap size="middle">
            <div>
              {filterLabel('繳款日期起 (YYYY-MM-DD)')}
              <Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('繳款日期迄')}
              <Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('客戶代號起')}
              <Input value={customerFrom} onChange={e => setCustomerFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('客戶代號迄')}
              <Input value={customerTo} onChange={e => setCustomerTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('銀行代號起')}
              <Input value={bankFrom} onChange={e => setBankFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('銀行代號迄')}
              <Input value={bankTo} onChange={e => setBankTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('部門代號起')}
              <Input value={deptFrom} onChange={e => setDeptFrom(e.target.value)} placeholder="起" style={{ width: 100 }} allowClear />
            </div>
            <div>
              {filterLabel('部門代號迄')}
              <Input value={deptTo} onChange={e => setDeptTo(e.target.value)} placeholder="迄" style={{ width: 100 }} allowClear />
            </div>
            <div>
              {filterLabel('繳款類別起')}
              <Input value={categoryFrom} onChange={e => setCategoryFrom(e.target.value)} placeholder="起" style={{ width: 80 }} allowClear />
            </div>
            <div>
              {filterLabel('繳款類別迄')}
              <Input value={categoryTo} onChange={e => setCategoryTo(e.target.value)} placeholder="迄" style={{ width: 80 }} allowClear />
            </div>
            <div>
              {filterLabel('水單號碼起')}
              <Input value={slipNoFrom} onChange={e => setSlipNoFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('水單號碼迄')}
              <Input value={slipNoTo} onChange={e => setSlipNoTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            {groupCounts && groupCounts.length > 0 && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                分組筆數：{groupCounts.map(g => `${g.name || g.code}(${g.count})`).join(', ')}
              </div>
            )}
            <Table<ReportRow>
              columns={columns}
              dataSource={data}
              rowKey={(r, i) => `${r.FG110_01}-${i}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 1500 }}
              style={{ marginBottom: 16 }}
            />
            {currencySummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>幣別合計 (台幣合計: {twdTotal?.toLocaleString('zh-TW')})</div>
                <Table<CurrencySummary>
                  columns={currColumns}
                  dataSource={currencySummary}
                  rowKey="FG110_25"
                  size="small"
                  pagination={false}
                  scroll={{ x: 500 }}
                  style={{ marginBottom: 16 }}
                />
              </>
            )}
            {delayStats.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>員工延遲天數統計</div>
                <Table<DelayStats>
                  columns={delayColumns}
                  dataSource={delayStats}
                  rowKey="employee_code"
                  size="small"
                  pagination={false}
                  scroll={{ x: 400 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default G305Form;
